import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import store from "@/store";
import {EmployeeState} from "@/interfaces/state";
import {Employee} from "@/interfaces/entity";
import {
    createEmployee, deleteEmployee,
    listEmployee,
    singleEmployee,
    updateEmployee
} from "@/modules/employees/services";
import {IdMapper} from "@/interfaces/state/base";
import {PaginationMeta} from "@/interfaces/common";
import {EmployeeParams} from "@/modules/employees/params";
import { union } from 'lodash';

@Module({ dynamic: true, name: "employee", store })
class EmployeeModule extends VuexModule implements EmployeeState {
    data: IdMapper<Employee> = {};
    ids: string[] = [];
    meta: PaginationMeta = new PaginationMeta();
    params: EmployeeParams = new EmployeeParams();
    type = {};

    @Mutation
    private UPDATE_EMPLOYEE_PARAMS(payload: any) {
        this.params = payload;
    }

    @Mutation
    private MAP_EMPLOYEE({payload, refresh = false}) {
        if (refresh) {
            this.data = {} as any;
            this.ids = [] as any;
            this.type = {} as any;
        }
        this.meta = payload.meta;
        payload.datas?.forEach((employee: Employee, index: number) => {
            this.data[employee.rowGuid!] = {...employee, isDeleting: false, stt: index + 1} as any;
            this.ids = union(this.ids, [employee.rowGuid]);
            if (!this.type[employee.type]) {
                this.type[employee.type] = [];
            }
            this.type[employee.type] = union(this.type[employee.type], [employee.rowGuid]);
        })
    }

    @Mutation
    private ADD_EMPLOYEE(payload: Employee) {
        if (!payload || this.ids.includes(payload.rowGuid) || this.data[payload.rowGuid]) return;
        this.ids = [ ...this.ids, payload.rowGuid ];
        this.data = { ...this.data, [payload.rowGuid]: {...payload, isDeleting: false} }
    }

    @Mutation
    private UPDATE_EMPLOYEE(payload: Employee) {
        if (!this.data[payload.rowGuid] || !this.ids.includes(payload.rowGuid)) return;
        this.data[payload.rowGuid] = { ...payload, isDeleting: false }
    }

    @Mutation
    private DELETE_EMPLOYEE(payload: Employee) {
        if (!this.data[payload.rowGuid] || !this.ids.includes(payload.rowGuid)) return;
        this.ids = this.ids.filter((id: string) => id !== payload.rowGuid);
        delete this.data[payload.rowGuid];
    }

    @Mutation
    private START_DELETE_EMPLOYEE(id: string) {
        this.data[id].isDeleting = true;
    }

    @Mutation
    private END_DELETE_EMPLOYEE(id: string) {
        this.data[id].isDeleting = false;
    }

    @Action({ rawError: true })
    public updateEmployeeParams(payload: any) {
        this.UPDATE_EMPLOYEE_PARAMS(payload);
    }

    @Action({ rawError: true })
    public addEmployee(payload: Employee) {
        this.ADD_EMPLOYEE(payload);
    }

    @Action({ rawError: true })
    public async createEmployee(form: any) {
        const { data } = await createEmployee(form);
        this.ADD_EMPLOYEE(data.data);
    }

    @Action({ rawError: true })
    public async listEmployee({params, refresh = false}) {
        const { data } = await listEmployee(params);
        this.MAP_EMPLOYEE({ payload: data, refresh });
    }

    @Action({ rawError: true })
    public async singleEmployee(id: string) {
        const { data } = await singleEmployee(id);
        return data.data;
    }

    @Action({ rawError: true })
    public async updateEmployee(dataReq: any) {
        const { data } = await updateEmployee(dataReq.rowGuid, dataReq);
        this.UPDATE_EMPLOYEE(data.data);
    }

    @Action({ rawError: true })
    public async deleteEmployee(id: string) {
        this.START_DELETE_EMPLOYEE(id);
        const { data } = await deleteEmployee(id);
        this.END_DELETE_EMPLOYEE(id);
        this.DELETE_EMPLOYEE(data.data);
    }
}

export const EmployeeStore = getModule(EmployeeModule);
